// PatientGallery.jsx
import React, { useState, useEffect } from 'react';
import styles from './galerie.module.css';
import { useNavigate } from 'react-router';
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import p1 from '../../assets/img/profile.png';
import p2 from '../../assets/img/front no smiling.png';
import p3 from '../../assets/img/front smiling.png';
import h1 from '../../assets/img/man profile.png';
import h2 from '../../assets/img/man front no smiling.png';
import h3 from '../../assets/img/man font smiling.png';
import p4 from '../../assets/img/Upper.png';
import p5 from '../../assets/img/Lower.png';
import p6 from '../../assets/img/right side.png';
import p7 from '../../assets/img/front teeth.png';
import p8 from '../../assets/img/left side.png';

const PatientGallery = ({ onBack, patient }) => {
  const navigate = useNavigate();
  const [patientImages, setPatientImages] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [patientCreationDate, setPatientCreationDate] = useState(null);
  const [photoSuivi, setPhotoSuivi] = useState([]);
  const [questionnaireImages, setQuestionnaireImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  
  // Format date safely - handling both Firestore timestamps and regular dates
  const formatDate = (dateValue) => {
    if (!dateValue) return new Date().toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    // If it's already a formatted string (from filename extraction), just return the date part
    if (
      typeof dateValue === 'string' &&
      (/^\d{2}\/\d{2}\/\d{4}( \d{2}:\d{2}:\d{2})?$/.test(dateValue))
    ) {
      // Only keep the date part
      return dateValue.slice(0, 10);
    }
    try {
      if (dateValue && typeof dateValue.toDate === 'function') {
        return dateValue.toDate().toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
      if (dateValue instanceof Date) {
        return dateValue.toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
      // Try to parse as ISO or other string date
      const parsed = new Date(dateValue);
      if (!isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
      return dateValue; // fallback: show as is
    } catch (error) {
      return new Date().toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
  };
  
  // Get appropriate gender-specific placeholder based on patient gender
  const getImagePlaceholder = (imageName) => {
    if (['img1', 'img2', 'img3'].includes(imageName)) {
      if (patient?.gender === 'Female') {
        switch(imageName) {
          case 'img1': return p1;
          case 'img2': return p2;
          case 'img3': return p3;
          default: return p1;
        }
      } else {
        switch(imageName) {
          case 'img1': return h1;
          case 'img2': return h2;
          case 'img3': return h3;
          default: return h1;
        }
      }
    }
    
    switch(imageName) {
      case 'img4': return p4;
      case 'img6': return p5;
      case 'img7': return p6;
      case 'img8': return p7;
      case 'img9': return p8;
      default: return p1;
    }
  };

  // Helper to extract date from filename in URL (e.g., 2025-06-20_02-24-55.jpg)
  const extractDateFromUrl = (url) => {
    const match = url && url.match(/(\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2})/);
    if (match) {
      const [datePart, timePart] = match[0].split('_');
      // Format: DD/MM/YYYY HH:MM:SS
      const [year, month, day] = datePart.split('-');
      const formatted = `${day}/${month}/${year} ${timePart.replace(/-/g, ':')}`;
      return formatted;
    }
    return null;
  };

  useEffect(() => {
    setIsLoading(true);
    setPhotoSuivi([]);
    setQuestionnaireImages([]);
    if (patient?.id) {
      const unsubscribe = onSnapshot(
        doc(db, "patients", patient.id),
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            if (data.images) {
              setPatientImages(data.images);
            } else {
              setPatientImages(null);
            }
            setPatientCreationDate(data.createdAt);
            // photo_suivi
            if (Array.isArray(data.photo_suivi)) {
              setPhotoSuivi(data.photo_suivi.map(item => {
                if (typeof item === 'string') {
                  // Try to extract date from URL
                  const extracted = extractDateFromUrl(item);
                  return { url: item, date: extracted || data.createdAt };
                } else if (item && item.url) {
                  // Use date from object, or extract from URL, or fallback
                  const extracted = extractDateFromUrl(item.url);
                  return { url: item.url, date: item.date || extracted || data.createdAt };
                }
                return null;
              }).filter(Boolean));
            }
            // questionnaires
            if (Array.isArray(data.questionnaires)) {
              const qImgs = [];
              data.questionnaires.forEach(q => {
                const imgUrl = q?.reponses?.question5;
                if (imgUrl && typeof imgUrl === 'string' && imgUrl.startsWith('http')) {
                  // Use q.date, or extract from URL, or fallback to createdAt
                  const extracted = extractDateFromUrl(imgUrl);
                  qImgs.push({ url: imgUrl, date: q.date || extracted || data.createdAt });
                }
              });
              setQuestionnaireImages(qImgs);
            }
          } else {
            setPatientImages(null);
            setPatientCreationDate(null);
            setPhotoSuivi([]);
            setQuestionnaireImages([]);
          }
          setIsLoading(false);
        },
        (error) => {
          setIsLoading(false);
        }
      );
      return () => unsubscribe();
    } else {
      setIsLoading(false);
    }
  }, [patient]);

  const createGalleryItems = () => {
    const imageMapping = [
      { id: 1, imgKey: 'img1', title: 'Portrait de profil au repos' },
      { id: 2, imgKey: 'img2', title: 'Portrait de face au repos' },
      { id: 3, imgKey: 'img3', title: 'Portrait de face au sourire' },
      { id: 4, imgKey: 'img4', title: 'Occlusale supérieure' },
      { id: 5, imgKey: 'img6', title: 'Occlusale inférieure' },
      { id: 6, imgKey: 'img7', title: 'Intra-orale latérale droite' },
      { id: 7, imgKey: 'img8', title: 'Intra-orale de face' },
      { id: 8, imgKey: 'img9', title: 'Intra-orale latérale gauche' }
    ];

    return imageMapping.map(item => {
      const imageUrl = patientImages?.[item.imgKey] || getImagePlaceholder(item.imgKey);
      let date = null;
      if (patientImages?.[item.imgKey]) {
        // Try to extract date from URL
        date = extractDateFromUrl(patientImages[item.imgKey]);
      }
      return {
        id: item.id,
        date: formatDate(date || patientCreationDate),
        title: item.title,
        thumbnail: imageUrl,
        imgKey: item.imgKey
      };
    });
  };

  const galleryItems = createGalleryItems();

  return (
    <div className={styles.galleryContainer}>
      <div className={styles.header}>
        <h3>
          <span className={styles.backIcon} onClick={onBack}>◀</span>
          Galerie Photos
        </h3>
      </div>
      <div className={styles.photoSection}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p>Chargement des images...</p>
          </div>
        ) : (
          <>
            {/* Main patient images */}
            <div className={styles.photoGrid}>
              {galleryItems.map(item => (
                <div key={item.id} className={styles.photoUploadBox}>
                  <div className={styles.dateLabel}>{item.date}</div>
                  <div className={styles.imageContainer}>
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      className={styles.galleryImage}
                      onError={e => { e.target.src = getImagePlaceholder(item.imgKey); }}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setPreviewImage(item.thumbnail);
                        setPreviewVisible(true);
                      }}
                    />
                  </div>
                  <div className={styles.photoLabel}>{item.title}</div>
                </div>
              ))}
            </div>
            {/* photo_suivi images */}
            {photoSuivi.length > 0 && (
              <>
                <h4 style={{marginTop: 24, marginBottom: 8}}>My Diamond app</h4>
                <div className={styles.photoGrid}>
                  {photoSuivi.map((item, idx) => (
                    <div key={item.url + idx} className={styles.photoUploadBox}>
                      <div className={styles.dateLabel}>{formatDate(item.date)}</div>
                      <div className={styles.imageContainer}>
                        <img
                          src={item.url}
                          alt="My Diamond app"
                          className={styles.galleryImage}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setPreviewImage(item.url);
                            setPreviewVisible(true);
                          }}
                        />
                      </div>
                      <div className={styles.photoLabel}>My Diamond app</div>
                    </div>
                  ))}
                </div>
              </>
            )}
            {/* questionnaire images */}
            {questionnaireImages.length > 0 && (
              <>
                <h4 style={{marginTop: 24, marginBottom: 8}}>Questionnaire</h4>
                <div className={styles.photoGrid}>
                  {questionnaireImages.map((item, idx) => (
                    <div key={item.url + idx} className={styles.photoUploadBox}>
                      <div className={styles.dateLabel}>{formatDate(item.date)}</div>
                      <div className={styles.imageContainer}>
                        <img
                          src={item.url}
                          alt="Questionnaire"
                          className={styles.galleryImage}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setPreviewImage(item.url);
                            setPreviewVisible(true);
                          }}
                        />
                      </div>
                      <div className={styles.photoLabel}>My Diamond app</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
      {/* Custom Image Preview Modal */}
      {previewVisible && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setPreviewVisible(false)}
        >
          <div
            style={{
              position: 'relative',
              background: 'transparent',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewVisible(false)}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: 'rgba(0,0,0,0.6)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: 32,
                height: 32,
                fontSize: 20,
                cursor: 'pointer',
                zIndex: 2
              }}
              aria-label="Close preview"
            >
              ×
            </button>
            {previewImage && (
              <img
                src={previewImage}
                alt="Preview"
                style={{
                  maxWidth: '80vw',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  borderRadius: 8
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientGallery;